import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';

const Subscription = ({ onBack }) => {
  const { user, logout } = useAuth();
  const [showMenu, setShowMenu] = useState(false);
  const [currentPlan] = useState('premium'); // premium, basic, free

  const plans = [
    {
      id: 'free',
      name: 'Free',
      price: '$0',
      period: 'forever',
      features: [
        'Access to 3 videos per month',
        'Standard definition quality',
        'Community support',
        'Basic content library'
      ],
      color: 'from-gray-600 to-gray-700'
    },
    {
      id: 'basic',
      name: 'Basic',
      price: '$9.99',
      period: 'per month',
      features: [
        'Access to 20 videos per month',
        'High definition quality',
        'Email support',
        'Expanded content library',
        'Download for offline viewing'
      ],
      color: 'from-blue-600 to-blue-700',
      popular: false
    },
    {
      id: 'premium',
      name: 'Premium',
      price: '$19.99',
      period: 'per month',
      features: [
        'Unlimited video access',
        '4K Ultra HD quality',
        'Priority support 24/7',
        'Full content library',
        'Download for offline viewing',
        'Exclusive masterclasses',
        'Early access to new content'
      ],
      color: 'from-red-600 to-purple-600',
      popular: true
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      {/* Navigation Bar */}
      <nav className="bg-gray-900/80 backdrop-blur-lg border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo & Back Button */}
            <div className="flex items-center space-x-4">
              <button
                onClick={onBack}
                className="flex items-center text-gray-400 hover:text-white transition"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span className="ml-2 hidden sm:inline">Back</span>
              </button>
              <div className="flex items-center">
                <div className="bg-gradient-to-r from-red-600 to-purple-600 p-2 rounded-lg">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                </div>
                <span className="ml-3 text-xl font-bold text-white">Subscription</span>
              </div>
            </div>

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="flex items-center space-x-3 px-4 py-2 rounded-lg hover:bg-gray-800 transition"
              >
                <div className="w-10 h-10 bg-gradient-to-r from-red-600 to-purple-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-semibold">
                    {user?.name?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
                <span className="text-white font-medium hidden sm:block">{user?.name}</span>
                <svg className={`w-4 h-4 text-gray-400 transition-transform ${showMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-gray-800 rounded-lg shadow-xl border border-gray-700 py-2">
                  <div className="px-4 py-2 border-b border-gray-700">
                    <p className="text-sm text-gray-400">Signed in as</p>
                    <p className="text-sm font-medium text-white truncate">{user?.email}</p>
                  </div>
                  <button
                    onClick={logout}
                    className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-gray-700 transition"
                  >
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-white mb-2">Choose Your Plan</h1>
          <p className="text-gray-400 text-lg">Select the perfect plan for your learning journey</p>
        </div>

        {/* Current Plan Banner */}
        <div className="bg-gradient-to-r from-green-600/20 to-emerald-600/20 border border-green-600/50 rounded-xl p-6 mb-12">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="bg-green-600 p-3 rounded-full">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">Current Plan: Premium</h3>
                <p className="text-gray-400 text-sm">Next billing date: January 19, 2026</p>
              </div>
            </div>
            <button className="px-6 py-2 border border-red-500 text-red-500 rounded-lg hover:bg-red-500/10 transition">
              Cancel Plan
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative bg-gray-800/50 backdrop-blur-lg rounded-2xl p-8 border ${
                plan.id === currentPlan
                  ? 'border-green-600 shadow-2xl shadow-green-600/20'
                  : 'border-gray-700'
              } ${plan.popular ? 'transform scale-105' : ''}`}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-red-600 to-purple-600 text-white text-xs font-bold px-4 py-1 rounded-full shadow-lg">
                    MOST POPULAR
                  </span>
                </div>
              )}

              {/* Current Plan Badge */}
              {plan.id === currentPlan && (
                <div className="absolute -top-4 right-4">
                  <span className="bg-green-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                    ACTIVE
                  </span>
                </div>
              )}

              {/* Plan Header */}
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                <div className="mb-4">
                  <span className="text-5xl font-bold text-white">{plan.price}</span>
                  <span className="text-gray-400 text-sm ml-2">/ {plan.period}</span>
                </div>
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-300 text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <button
                disabled={plan.id === currentPlan}
                className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-300 ${
                  plan.id === currentPlan
                    ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                    : `bg-gradient-to-r ${plan.color} text-white hover:shadow-lg transform hover:scale-105`
                }`}
              >
                {plan.id === currentPlan ? 'Current Plan' : 'Upgrade Now'}
              </button>
            </div>
          ))}
        </div>

        {/* Payment History */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-white mb-6">Payment History</h2>
          <div className="bg-gray-800/50 backdrop-blur-lg rounded-xl border border-gray-700 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-900/50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Date</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Description</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Amount</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Invoice</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                <tr className="hover:bg-gray-700/30 transition">
                  <td className="px-6 py-4 text-sm text-gray-300">Dec 19, 2025</td>
                  <td className="px-6 py-4 text-sm text-gray-300">Premium Subscription</td>
                  <td className="px-6 py-4 text-sm text-white font-semibold">$19.99</td>
                  <td className="px-6 py-4">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-500">
                      Paid
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <button className="text-red-500 hover:text-red-400 text-sm font-medium">
                      Download
                    </button>
                  </td>
                </tr>
                <tr className="hover:bg-gray-700/30 transition">
                  <td className="px-6 py-4 text-sm text-gray-300">Nov 19, 2025</td>
                  <td className="px-6 py-4 text-sm text-gray-300">Premium Subscription</td>
                  <td className="px-6 py-4 text-sm text-white font-semibold">$19.99</td>
                  <td className="px-6 py-4">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-500">
                      Paid
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <button className="text-red-500 hover:text-red-400 text-sm font-medium">
                      Download
                    </button>
                  </td>
                </tr>
                <tr className="hover:bg-gray-700/30 transition">
                  <td className="px-6 py-4 text-sm text-gray-300">Oct 19, 2025</td>
                  <td className="px-6 py-4 text-sm text-gray-300">Premium Subscription</td>
                  <td className="px-6 py-4 text-sm text-white font-semibold">$19.99</td>
                  <td className="px-6 py-4">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-500">
                      Paid
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <button className="text-red-500 hover:text-red-400 text-sm font-medium">
                      Download
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Subscription;
